import { Card, Progress, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ProjectStatEntry, SourceStatEntry } from '@/api/uploadRecords';
import { SOURCE_LABELS } from '@/constants/upload';

const { Text } = Typography;

const rateColor = (rate: number) => (rate >= 90 ? '#52c41a' : rate >= 70 ? '#fa8c16' : '#ff4d4f');

const rateColumn = <T extends { success_rate: number }>(
  title: string,
  colorByValue: boolean,
): ColumnsType<T>[number] => ({
  title,
  key: 'success_rate',
  sorter: (a, b) => a.success_rate - b.success_rate,
  render: (_, row) => {
    const rate = row.success_rate;
    const color = colorByValue ? rateColor(rate) : '#1677ff';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Progress
          percent={rate}
          size="small"
          strokeColor={color}
          showInfo={false}
          style={{ flex: 1, minWidth: 60, margin: 0 }}
        />
        <Text style={{ color, fontWeight: 600, whiteSpace: 'nowrap', minWidth: 44 }}>
          {rate}%
        </Text>
      </div>
    );
  },
  width: 160,
});

const countColumn = <T,>(
  title: string,
  key: keyof T & string,
  color: (row: T) => string = () => '#595959',
): ColumnsType<T>[number] => ({
  title,
  key,
  sorter: (a, b) => Number(a[key]) - Number(b[key]),
  render: (_, row) => <Text style={{ color: color(row), fontWeight: 600 }}>{String(row[key])}</Text>,
  width: 90,
  align: 'center',
});

const sourceColumns: ColumnsType<SourceStatEntry> = [
  {
    title: '来源',
    dataIndex: 'source',
    key: 'source',
    render: (value: string) => {
      const meta = SOURCE_LABELS[value];
      return meta ? <Tag color={meta.color}>{meta.label}</Tag> : value;
    },
    width: 120,
  },
  countColumn<SourceStatEntry>('总次数', 'total', () => '#1677ff'),
  countColumn<SourceStatEntry>('成功', 'done', () => '#52c41a'),
  countColumn<SourceStatEntry>('失败', 'failed', (row) =>
    row.failed > 0 ? '#ff4d4f' : '#8c8c8c'),
  countColumn<SourceStatEntry>('上传中', 'uploading', (row) =>
    row.uploading > 0 ? '#fa8c16' : '#8c8c8c'),
  rateColumn<SourceStatEntry>('成功率', true),
  {
    title: '失败率',
    key: 'fail_rate',
    sorter: (a, b) => a.fail_rate - b.fail_rate,
    render: (_, row) => (
      <Text style={{ color: row.fail_rate > 0 ? '#ff4d4f' : '#8c8c8c' }}>{row.fail_rate}%</Text>
    ),
    width: 90,
    align: 'center',
  },
];

const projectColumns: ColumnsType<ProjectStatEntry> = [
  {
    title: '项目',
    dataIndex: 'project_name',
    key: 'project_name',
    sorter: (a, b) => a.project_name.localeCompare(b.project_name),
    render: (name: string) => <Text strong>{name || '-'}</Text>,
    width: 180,
  },
  countColumn<ProjectStatEntry>('总次数', 'total', () => '#1677ff'),
  countColumn<ProjectStatEntry>('成功', 'done', () => '#52c41a'),
  countColumn<ProjectStatEntry>('失败', 'failed', (row) =>
    row.failed > 0 ? '#ff4d4f' : '#8c8c8c'),
  countColumn<ProjectStatEntry>('上传中', 'uploading', (row) =>
    row.uploading > 0 ? '#fa8c16' : '#8c8c8c'),
  rateColumn<ProjectStatEntry>('成功率', true),
  {
    title: '失败率',
    key: 'fail_rate',
    sorter: (a, b) => a.fail_rate - b.fail_rate,
    render: (_, row) => (
      <Text style={{ color: row.fail_rate > 0 ? '#ff4d4f' : '#8c8c8c' }}>{row.fail_rate}%</Text>
    ),
    width: 90,
    align: 'center',
  },
];

const UploadStatsTables = ({
  bySource,
  byProject,
}: {
  bySource: SourceStatEntry[];
  byProject: ProjectStatEntry[];
}) => (
  <>
    <Card title="来源明细" style={{ borderRadius: 8, marginBottom: 16 }}>
      <Table<SourceStatEntry>
        rowKey="source"
        dataSource={bySource}
        columns={sourceColumns}
        pagination={false}
        size="small"
        scroll={{ x: 760 }}
        locale={{ emptyText: '暂无数据' }}
      />
    </Card>
    <Card title="项目明细" style={{ borderRadius: 8 }}>
      <Table<ProjectStatEntry>
        rowKey="project_id"
        dataSource={byProject}
        columns={projectColumns}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        size="small"
        scroll={{ x: 820 }}
        locale={{ emptyText: '暂无数据' }}
      />
    </Card>
  </>
);

export default UploadStatsTables;
